import React, { useState, useEffect } from 'react';
import {
    Box,
    Typography,
    Button,
    IconButton,
    Paper,
    Grid,
    Dialog,
    DialogContent,
    CircularProgress
} from '@mui/material';
import { useNavigate, useParams } from 'react-router-dom';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import AddIcon from '@mui/icons-material/Add';
import ChevronLeftIcon from '@mui/icons-material/ChevronLeft';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import CloseIcon from '@mui/icons-material/Close';
import ImageIcon from '@mui/icons-material/Image';
import { getPhotosAndAlbums } from '../../../../Utils/apiService';
import routes from '../../../../Utils/routes';
import KenLoader from '../../../../GlobalComponents/KenLoader';
import PhotoContainer from '../PhotoContainer';

interface FileRecord {
    Id: string;
    Name: string;
    File_Link__c: string;
    CreatedDate: string;
    File_Admin__r?: {
        Profile_Picture_URL__c: string;
    };
}

interface FolderData {
    Id: string;
    Name: string;
    Folder_Type__c: string;
    Folder_Access__c: string;
    CreatedDate: string;
    LastModifiedDate: string;
    Files__r?: {
        totalSize: number;
        records: FileRecord[];
    };
}

const AlbumViewer: React.FC = () => {
    const { folderId } = useParams<{ folderId: string }>();
    const navigate = useNavigate();
    const [loading, setLoading] = useState(true);
    const [albumData, setAlbumData] = useState<FolderData | null>(null);
    const [images, setImages] = useState<FileRecord[]>([]);
    const [currentImageIndex, setCurrentImageIndex] = useState(0);
    const [sliderOpen, setSliderOpen] = useState(false);
    const [photosData, setPhotosData] = useState<any[]>([]);
    const [albumsData, setAlbumsData] = useState<any[]>([]);
    const [imageLoading, setImageLoading] = useState(false);

    useEffect(() => {
        console.log("Folder ID from URL params:", folderId);

        // Existing fetch code...
    }, [folderId]);

    useEffect(() => {
        // Fetch album data based on folderId
        const fetchAlbumData = async () => {
            try {
                setLoading(true);
                // Replace the fetch with the imported API function
                const response = await getPhotosAndAlbums();
                console.log("API Response:", response);

                // Find the matching album from the response
                let foundAlbum = null;
                if (response && response.success) {
                    console.log("All albums:", response.data.data.albums);
                    foundAlbum = response.data.data.albums.find((album: FolderData) => album.Id === folderId);
                    console.log("Found album:", foundAlbum);
                }

                if (foundAlbum) {
                    setAlbumData(foundAlbum);

                    // Filter for valid images (with valid File_Link__c)
                    if (foundAlbum.Files__r && foundAlbum.Files__r.records) {
                        console.log("All files in album:", foundAlbum.Files__r.records);
                        const validImages = foundAlbum.Files__r.records.filter((file: { File_Link__c: string; }) =>
                            file.File_Link__c &&
                            file.File_Link__c.startsWith('http') &&
                            !file.File_Link__c.includes('Insert failed')
                        );
                        setImages(validImages);
                    }
                } else {
                    console.error("Album not found with ID:", folderId);
                }
            } catch (error) {
                console.error("Error fetching album data:", error);
            } finally {
                setLoading(false);
            }
        };

        if (folderId) {
            fetchAlbumData();
        }
    }, [folderId]);

    useEffect(() => {
        // Fetch all photos and albums for the upload navigation
        const fetchAllPhotosAndAlbums = async () => {
            try {
                const response = await getPhotosAndAlbums();
                if (response && response.data && response.data.data) {
                    setPhotosData(response.data.data.photos || []);
                    setAlbumsData(response.data.data.albums || []);
                }
            } catch (error) {
                console.error("Error fetching all photos and albums:", error);
            }
        };

        fetchAllPhotosAndAlbums();
    }, []);

    const handleAddPhotos = () => {
        const albumsInfo = albumsData.map(album => ({
            id: album.Id,
            name: album.Name,
        }));

        const photosInfo = photosData.map(photo => ({
            id: photo.Id,
            name: photo.Name,
            fileLink: photo.File_Link__c,
            description: photo.Description__c,
            location: photo.Location__c,
            createdDate: photo.CreatedDate
        }));

        navigate(routes.PhotoAlbumUpload, {
            state: {
                albums: albumsInfo,
                photos: photosInfo,
                currentAlbumId: folderId,
                selectedAlbum: folderId
            }
        });
    };

    const openSlider = (index: number) => {
        setCurrentImageIndex(index);
        setSliderOpen(true);
    };

    const closeSlider = () => {
        setSliderOpen(false);
    };

    const nextImage = () => {
        setImageLoading(true);
        setCurrentImageIndex((prevIndex) =>
            prevIndex === images.length - 1 ? 0 : prevIndex + 1
        );
    };

    const prevImage = () => {
        setImageLoading(true);
        setCurrentImageIndex((prevIndex) =>
            prevIndex === 0 ? images.length - 1 : prevIndex - 1
        );
    };

    if (loading) {
        return (
            <Box
                display="flex"
                justifyContent="center"
                alignItems="center"
                sx={{
                    height: '83.5vh',
                    backgroundColor: '#fff',
                    borderRadius: '9px',
                    width: '100%'
                }}
            >
                <KenLoader />
            </Box>
        );
    }

    return (
        <Paper
            elevation={0}
            sx={{
                backgroundColor: '#fff',
                borderRadius: '9px',
                height: '83.5vh',
                width: '100%',
                overflow: 'hidden',
                display: 'flex',
                flexDirection: 'column',
                '@media (min-width: 1280px) and (max-width: 1300px)': {
                    height: '81vh',
                },
                '@media (max-width: 1600px)': {
                    height: '83.5vh',
                },
                '@media (max-width: 1360px)': {
                    height: '100vh',
                }
            }}
        >
            <Box display="flex" alignItems="center" justifyContent={"space-between"} p={3} pb={2}>
                <div className='d-flex justify-content-between align-items-center'>
                <IconButton
                    onClick={() => {
                        localStorage.setItem('photoAlbumActiveTab', '1');
                        navigate(-1);
                    }}
                    sx={{ color: '#123A6F', mr: 1 }}
                >
                    <ArrowBackIcon />
                </IconButton>
                <Typography variant="h5" fontWeight={500} color="#123A6F" sx={{ flex: 1 }}>
                    {albumData?.Name || 'Album'}
                </Typography>
                </div>
                <Button
                    variant="contained"
                    color='primary'
                    size='medium'
                    startIcon={<AddIcon />}
                    onClick={handleAddPhotos}
                  
                >
                    Add more photos
                </Button>
            </Box>

            <Box
                sx={{
                    flex: 1,
                    overflowY: 'auto',
                    overflowX: 'hidden',
                    p: 3,
                    pt: 0
                }}
                className="scrollbar-cusgray"
            >
                {images.length === 0 ? (
                    <Box
                        display="flex"
                        flexDirection="column"
                        justifyContent="center"
                        alignItems="center"
                        sx={{
                            height: 'calc(100% - 20px)',
                            textAlign: 'center',
                            padding: 3
                        }}
                    >
                        <ImageIcon sx={{ fontSize: 56, color: '#123A6F', opacity: 0.6, mb: 1.5 }} />
                        <Typography variant="body2" color="#6F7787" mb={1}>
                            No images available
                        </Typography>
                        <Typography variant="body2" color="#6F7787" mb={2}>
                            Start by uploading your first image
                        </Typography>
                    </Box>
                ) : (
                    <>
                        <PhotoContainer
                            cardData={images.map((image) => ({
                                Id: image.Id,
                                Name: image.Name,
                                Description__c: '',
                                Location__c: '',
                                File_Link__c: image.File_Link__c,
                                File_Admin__r: {
                                    Id: '',
                                    Profile_Picture_URL__c: image.File_Admin__r?.Profile_Picture_URL__c || '',
                                    Name: '',
                                },
                                Alumni_Photo_Tags__r: {
                                    records: [],
                                },
                            }))}
                        />
                    </>
                )}
            </Box>

            {/* Image Slider Dialog */}
            <Dialog
                open={sliderOpen}
                onClose={closeSlider}
                maxWidth={false}
                fullWidth={false}
                PaperProps={{
                    sx: {
                        borderRadius: 3,
                        overflow: 'hidden',
                        width: '800px',
                        height: '600px',
                        maxWidth: '90vw',
                        maxHeight: '90vh',
                        margin: { xs: '8px', sm: '16px', md: '24px' },
                    }
                }}
            >
                <DialogContent sx={{ p: 0, position: 'relative', height: '100%', display: 'flex', flexDirection: 'column' }}>
                    <Box display="flex" justifyContent="flex-end" p={1} position="absolute" right={0} zIndex={3} sx={{ backgroundColor: 'rgba(255,255,255,0.8)' }}>
                        <IconButton onClick={closeSlider} sx={{ color: '#000', fontSize: "12px" }}>
                            <CloseIcon />
                        </IconButton>
                    </Box>

                    {images.length > 1 && (
                        <>
                            <IconButton
                                onClick={() => {
                                    setImageLoading(true);
                                    prevImage();
                                }}
                                sx={{
                                    position: 'absolute',
                                    left: 16,
                                    top: '50%',
                                    transform: 'translateY(-50%)',
                                    zIndex: 3,
                                    backgroundColor: 'rgba(255,255,255,0.8)',
                                    '&:hover': {
                                        backgroundColor: 'rgba(255,255,255,0.9)',
                                    },
                                }}
                            >
                                <ChevronLeftIcon fontSize="large" />
                            </IconButton>

                            <IconButton
                                onClick={() => {
                                    setImageLoading(true);
                                    nextImage();
                                }}
                                sx={{
                                    position: 'absolute',
                                    right: 16,
                                    top: '50%',
                                    transform: 'translateY(-50%)',
                                    zIndex: 3,
                                    backgroundColor: 'rgba(255,255,255,0.8)',
                                    '&:hover': {
                                        backgroundColor: 'rgba(255,255,255,0.9)',
                                    },
                                }}
                            >
                                <ChevronRightIcon fontSize="large" />
                            </IconButton>
                        </>
                    )}

                    <Box sx={{ flexShrink: 0, p: 2, borderBottom: '1px solid #e0e0e0' }}>
                        <Typography
                            variant="h6"
                            fontSize={{ xs: 16, sm: 18 }}
                            fontWeight={600}
                            sx={{ mb: 1 }}
                        >
                            {images[currentImageIndex]?.Name}
                        </Typography>
                        <Typography variant="body2" fontSize={{ xs: 12, sm: 14 }} fontWeight={500} color="text.secondary">
                            {images[currentImageIndex]?.CreatedDate ? new Date(images[currentImageIndex]?.CreatedDate).toLocaleDateString() : ''}
                        </Typography>
                    </Box>

                    <Box sx={{ flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center', overflow: 'hidden', position: 'relative' }}>
                        {imageLoading && (
                            <Box
                                sx={{
                                    position: 'absolute',
                                    top: 0,
                                    left: 0,
                                    right: 0,
                                    bottom: 0,
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                    backgroundColor: 'rgba(255, 255, 255, 0.8)',
                                    zIndex: 2,
                                }}
                            >
                                <KenLoader />
                            </Box>
                        )}

                        {images.length > 0 && (
                            <Box
                                component="img"
                                src={images[currentImageIndex]?.File_Link__c}
                                alt={images[currentImageIndex]?.Name}
                                sx={{
                                    maxWidth: '100%',
                                    maxHeight: '100%',
                                    objectFit: 'contain',
                                    opacity: imageLoading ? 0 : 1,
                                    transition: 'opacity 0.3s ease-in-out',
                                }}
                                onLoad={() => setImageLoading(false)}
                                onError={(e) => {
                                    setImageLoading(false);
                                    const target = e.target as HTMLImageElement;
                                    target.style.display = 'none';
                                    const parent = target.parentElement;
                                    if (parent) {
                                        const fallbackElement = document.createElement('div');
                                        fallbackElement.style.display = 'flex';
                                        fallbackElement.style.justifyContent = 'center';
                                        fallbackElement.style.alignItems = 'center';
                                        fallbackElement.style.width = '100%';
                                        fallbackElement.style.height = '50vh';

                                        const iconElement = document.createElement('div');
                                        iconElement.innerHTML = `<svg style="width: 96px; height: 96px; color: #BDBDBD; opacity: 0.7;" viewBox="0 0 24 24"><path fill="currentColor" d="M21 19V5c0-1.1-.9-2-2-2H5c-1.1 0-2 .9-2 2v14c0 1.1.9 2 2 2h14c1.1 0 2-.9 2-2zM8.5 13.5l2.5 3.01L14.5 12l4.5 6H5l3.5-4.5z"></path></svg>`;

                                        fallbackElement.appendChild(iconElement);
                                        parent.appendChild(fallbackElement);
                                    }
                                }}
                            />
                        )}
                    </Box>

                    <Box
                        sx={{
                            flexShrink: 0,
                            display: 'flex',
                            justifyContent: 'space-between',
                            alignItems: 'center',
                            p: 2,
                            backgroundColor: '#f5f5f5',
                            borderTop: '1px solid #e0e0e0'
                        }}
                    >
                        <Typography variant="body2" color="text.secondary">
                            {images.length > 1 ? `${currentImageIndex + 1} of ${images.length}` : '1 of 1'}
                        </Typography>
                    </Box>
                </DialogContent>
            </Dialog>
        </Paper>
    );
};

export default AlbumViewer;
